import React, {
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import Button from "../../Common/Button/Button";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import TextInput from "../../Common/TextInput/TextInput";
import SelectInput from "../../Common/Select-Input/SelectInput";
import diceIcon from "../../Assets/Images/dice-icon.png";
import diceDarkIcon from "../../Assets/Images/dice-dark-icon.png";
import { DynamicContext } from "../../Context/DynamicContext";
import OptionData from "../../Assets/Json/StaticOptions.json";
import AutoComplete from "../../Common/AutoComplete/AutoComplete";
import backArrowIcon from "../../Assets/Images/arrow-back-icon.png";
import DynamicMultipleSelectCheckmarks from "../Multiple-Select-Checkmarks/MultipleSelectCheckmarks";
import DynamicCheckbox from "../../Common/Checkbox/Checkbox";
import compactIcon from "../../Assets/Images/compact-icon.svg";
import detailedIcon from "../../Assets/Images/detailed-icon.svg";
import "./DiceSearch.css";
import infoIcon from "../../Assets/Images/infoicon.svg";
import CustomTooltip from "../../Common/Tooltip/Tooltip";
import {
  DICE_SEARCH_BASE_URL,
  DICE_BASIC_SEARCH_URL,
  CCREATE_CAN,
  GET_CANDIDATES_BY_NOTES,
  DICE_CANDIDATE_PROFILE_VIEW,
  VYLABS_BASE_URL,
  DICE_SEARCH_LOGIN_URL,
  UPDATE_CANDIDATE_DETAILS,
  LINK_CANDIDATE_RESUME,
  GET_CANDIDATE_BY_ID,
  GET_VENDOR_DETAILS_BY_ID,
  VENDORS_SEARCH,
} from "../../helper/url_helper";
import DiceCard from "./DiceCard";
import AnimationPlayer from "../../Common/Animation-Player/AnimationPlayer";
import VybogTALLoaderAnimation from "../../Common/Dynamic-Animations/DynamicAnimations.js";
import DynamicSkeleton from "../../Common/Skeleton/Skeleton";
import NoDataFound from "../../Assets/Json/nodata-found-robot-animation.json";
import SidePopup from "../../Common/SidePopup/SidePopup";
import CandidateDetailsContainer from "../../Common/Candidate-Details-Container/CandidateDetailsContainer";
import { parseResumeHeadless } from "./DiceResumeParser.js";
import { get, post } from "../../helper/Api_helper";
import { Toast } from "../../Common/Toast-Snackbar/ToastSnackbar";
import envConfig from "../../helper/env_config.js";

function htmlToFile(html, fileName) {
  const blob = new Blob([html || ""], { type: "text/html" });
  const file = new File([blob], fileName, { type: "text/html" });
  file.localUrl = URL.createObjectURL(blob);
  return file;
}
const htmlToTextFile = htmlToFile;

function getCandidateFileName(candidateName, fallbackId, extension = "html") {
  const name = candidateName?.trim();
  if (!name || name.toLowerCase() === "unknown name") {
    return `dice_${fallbackId}.${extension}`;
  }
  const safeName = name.replace(/[/\\?%*:|"<>]/g, "").replace(/\s+/g, "_");
  return `${safeName || `dice_${fallbackId}`}.${extension}`;
}

function DiceSearch() {
  const { isDarkMode } = useContext(DynamicContext);
  const navigate = useNavigate();
  const { StaticOptions = {} } = OptionData || {};
  const {
    skills = [],
    DiceCompany = [],
    DiceEmploymentType = [],
    DiceWorkPermitVisa = [],
    DistanceUnit = [],
  } = StaticOptions || {};
  const [autoCompleteKey, setAutoCompleteKey] = useState(0);
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [advancedSearchPayload, setAdvancedSearchPayload] = useState(null);
  const [showPayloadPreview, setShowPayloadPreview] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedCandidateId, setSelectedCandidateId] = useState(null);
  const [isProcessingView, setIsProcessingView] = useState(false);
  const [localCandidate, setLocalCandidate] = useState(null);
  const [showLocalCandidatePopup, setShowLocalCandidatePopup] = useState(false);
  const [fallbackDicePopupOpen, setFallbackDicePopupOpen] = useState(false);
  const [diceProfileData, setDiceProfileData] = useState(null);
  const [abortController, setAbortController] = useState(null);
  const [searchError, setSearchError] = useState(null);
  const [diceCurrentPage, setDiceCurrentPage] = useState(1);
  const [hasMoreDiceResults, setHasMoreDiceResults] = useState(true);
  const [isLoadingMoreDice, setIsLoadingMoreDice] = useState(false);
  const [allDiceResults, setAllDiceResults] = useState([]);
  const observer = useRef();
  const [isEditingCandidate, setIsEditingCandidate] = useState(false);
  const [originalCandidateData, setOriginalCandidateData] = useState(null);
  const [editedCandidateData, setEditedCandidateData] = useState(null);
  const [vendorList, setVendorList] = useState([]);
  const [vendorOptionLoading, setVendorOptionLoading] = useState(false);
  const [assignedVendor, setAssignedVendor] = useState(null);
  const [diceAccessToken, setDiceAccessToken] = useState(null);
  const [hasCandidateId , setHasCandidateId] = useState(false);
  const [realCandidateId, setRealCandidateId] = useState();
  const [isImportFailed, setIsImportFailed] = useState(false);
  const [cardView, setCardView] = useState("compact");

  const diceLogin = useCallback(async () => {
    try {
      const response = await post(DICE_SEARCH_BASE_URL, DICE_SEARCH_LOGIN_URL);
      if (
        response &&
        response.userDetails &&
        response.userDetails.access_token
      ) {
        setDiceAccessToken(response.userDetails.access_token);
        return response.userDetails;
      } else {
        throw new Error("Invalid response from Dice login API");
      }
    } catch (error) {
      console.error("Dice login failed:", error);
      setDiceAccessToken(null);
      throw error;
    }
  }, []);

  useEffect(() => {
    diceLogin();
  }, [diceLogin]);

  const fetchVendors = async (inputValue = "") => {
    try {
      setVendorOptionLoading(true);
      const response = await get(
        VYLABS_BASE_URL,
        `${VENDORS_SEARCH}?vendor_name=${encodeURIComponent(inputValue)}`,
      );

      const { vendors = [] } = response;
      const fetchedVendors = Array.isArray(vendors)
        ? vendors
            .filter((v) => v?.id && v?.name)
            .map((v) => ({
              label: v.contact_person
                ? `${v.name} - ${v.contact_person}`
                : v.name,
              value: v.id,
            }))
        : [];

      setVendorList(fetchedVendors);
    } catch (err) {
      console.error("fetchVendors error", err);
      setVendorList([]);
    } finally {
      setVendorOptionLoading(false);
    }
  };

  const handleVendorInputChange = (_, value) => {
    if (value && value.length >= 3) {
      fetchVendors(value);
    } else {
      setVendorList([]);
    }
  };

  const lastCardRef = useCallback(
    (node) => {
      if (isLoadingMoreDice || loading) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMoreDiceResults) {
            loadMoreDiceResults();
          }
        },
        {
          root: null,
          rootMargin: "100px",
          threshold: 0.1,
        },
      );

      if (node) observer.current.observe(node);
    },
    [isLoadingMoreDice, loading, hasMoreDiceResults],
  );

  const handleEditCandidate = () => {
    if (!localCandidate) return;

    if (localCandidate?.isTempCandidate || localCandidate?.isLoadingResume) {
      Toast("error", "Please wait until candidate is saved.");
      return;
    }

    const getValidExperience = (value) => {
      const num = Number(value);
      if (isNaN(num)) return "";
      if (num < 0) return 0;
      return num;
    };

    const experienceValue = getValidExperience(
      localCandidate?.experience ?? localCandidate?.yearsOfExperience,
    );

    const formattedCandidate = {
      ...localCandidate,
      name: localCandidate.name || "",
      email: localCandidate.email || "",
      phone: localCandidate.phone || "",
      skills: Array.isArray(localCandidate.skills)
        ? localCandidate.skills
        : localCandidate.parsedResumeData?.Skills || [],
      designation: localCandidate.designation || "",
      experience: experienceValue,
      city: localCandidate.city || "",
      state: localCandidate.state || "",
      country: localCandidate.country || "",
      vendorID: localCandidate.vendorID || "",
    };

    setOriginalCandidateData({ ...localCandidate });
    setEditedCandidateData(formattedCandidate);
    setIsEditingCandidate(true);
  };

  const handleCandidateChange = (data) => {
    setEditedCandidateData((prev) => ({ ...prev, ...data }));
  };

  const saveCandidateChanges = async () => {
    if (!editedCandidateData) {
      Toast("error", "No changes to save");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      if (!token) {
        Toast("error", "Unauthorized");
        return;
      }

      const candidateData = { id: localCandidate.id };

      Object.keys(editedCandidateData).forEach((key) => {
        if (editedCandidateData[key] !== originalCandidateData[key]) {
          candidateData[key] = editedCandidateData[key];
        }
      });

      if (Object.keys(candidateData).length === 1) {
        Toast("info", "No changes were made.");
        setIsEditingCandidate(false);
        return;
      }

      Object.keys(candidateData).forEach((key) => {
        if (key === "preferredSalary") {
          candidateData[key] = Number(candidateData[key]);
        }
      });

      if (candidateData.experience !== undefined) {
        candidateData.yearsOfExperience = Number(candidateData.experience);
        delete candidateData.experience;
      }
      Object.keys(candidateData).forEach((key) => {
        if (candidateData[key] === "" || candidateData[key] == null) {
          delete candidateData[key];
        }
      });
      const response = await post(
        VYLABS_BASE_URL,
        UPDATE_CANDIDATE_DETAILS,
        candidateData,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response) {
        const updatedCandidate = { ...localCandidate, ...candidateData };

        if (
          candidateData.vendorID !== undefined &&
          candidateData.vendorID !== localCandidate.vendorID
        ) {
          if (candidateData.vendorID) {
            try {
              const vendorResponse = await get(
                VYLABS_BASE_URL,
                `${GET_VENDOR_DETAILS_BY_ID}?id=${candidateData.vendorID}`,
              );
              updatedCandidate.vendorDetails = vendorResponse?.vendor || null;
              setAssignedVendor(vendorResponse);
            } catch (err) {
              console.error("Error fetching vendor details:", err);
              updatedCandidate.vendorDetails = null;
              setAssignedVendor(null);
            }
          } else {
            updatedCandidate.vendorDetails = null;
            setAssignedVendor(null);
          }
        }
        setLocalCandidate(updatedCandidate);
        setIsEditingCandidate(false);
        setOriginalCandidateData(null);
        setEditedCandidateData(null);
        Toast("success", "Candidate updated successfully");
      }
    } catch (error) {
      console.error("Error updating candidate:", error);
      Toast("error", "Failed to update candidate");
    }
  };

  const resetCandidateEdit = () => {
    if (originalCandidateData) {
      setLocalCandidate(originalCandidateData);
    }
    setIsEditingCandidate(false);
    setOriginalCandidateData(null);
    setEditedCandidateData(null);
  };
  const formik = useFormik({
    initialValues: {
      searchKeywords: "",
      country: "United States",
      location: "",
      poratalName: "",
      skillsAS: [],
      jobTitleAS: "",
      yearsofEXPFrom: "",
      yearsofEXPto: "",
      distance: "",
      distanceUnit: "",
      socialProfiles: "",
      company: [],
      visa: [],
      employmentType: [],
      candidateLastActive: "",
      resumeLastUpdated: "",
      emailAS: "",
      phoneNumberAS: "",
      willingToRelocate: "",
    },
    onSubmit: async (values) => {
      if (!diceAccessToken) {
        Toast("error", "Please select an account to search.");
        return;
      }
      const hasKeywords = values.searchKeywords.trim() !== "";
      if (!hasKeywords) {
        Toast("error", "Please fill all required fields correctly.");
        return;
      }
      setDiceCurrentPage(1);
      setHasMoreDiceResults(true);
      setAllDiceResults([]);
      searchDiceCandidates(values, 1, false);
    },
  });

  const transformDiceProfileToCandidate = (profile, diceId) => {
    try {
      const profileData =
        typeof profile === "string" ? JSON.parse(profile) : profile;

      const name =
        profileData.fullName ||
        `${profileData.firstName || ""} ${profileData.lastName || ""}`.trim() ||
        "Unknown Name";
      const primaryEmail = Array.isArray(profileData.email)
        ? profileData.email[0]
        : "";
      const primaryPhone = Array.isArray(profileData.phone)
        ? profileData.phone[0]
        : "";
      const formattedPhone = primaryPhone ? `+1${primaryPhone}` : "";

      const location =
        profileData.locations?.[0]?.text ||
        `${profileData.city || ""}, ${profileData.region || ""}`.trim(", ") ||
        "";

      const uniqueSkills = [
        ...new Set(
          (profileData.skills || []).map((s) => s.skill).filter(Boolean),
        ),
      ];

      const yearsExp = profileData.yearsOfExperience || 0;
      const designation = profileData.currentJobTitle || "";
      const employer = profileData.currentCompany || "";

      const parsedResume = {
        BasicInfo: {
          Name: name,
          Email: primaryEmail,
          Phone: formattedPhone,
          CurrentLocationCity: location.split(",")[0]?.trim() || "",
          CurrentLocationCountry: profileData.country || "",
          YearsOfExperience: parseFloat(yearsExp) || 0,
        },
        Skills: uniqueSkills,
        EducationHistory: (profileData.education || []).map((edu) => ({
          Institute: edu.org || "",
          InstituteLocationCity: edu.location || "",
          InstituteLocationCountry: "",
          Degree: edu.degree || "",
          FieldOfStudy: "",
          GraduationDate: "",
          GPAOrTestScore: -1,
        })),
        CertificationsAndLicenses: [],
        CareerHistory: {
          JobDetails: (profileData.experience?.history || []).map((job) => ({
            JobTitle: job.title || "",
            JobCompany: job.org || "",
            JobLocationCity: "",
            JobLocationCountry: "",
            StartDate: job.periodStart
              ? `${job.periodStart.month}/${job.periodStart.year}`
              : "",
            EndDate: job.periodEnd
              ? `${job.periodEnd.month}/${job.periodEnd.year}`
              : "",
            CurrentlyWorking: !job.periodEnd,
            Description: "",
          })),
        },
        ProfileSummary: profileData.bio || "",
      };

      const candidate = {
        id: `temp_dice_${diceId}`,
        name: name,
        email: primaryEmail,
        secondary_email: "",
        phone: formattedPhone,
        secondary_phone: "",
        city: location.split(",")[0]?.trim() || "",
        state: profileData.region || "",
        country: profileData.country || "",
        parsed_resume: JSON.stringify(parsedResume),
        skills: uniqueSkills,
        status: "New Profile",
        contact_person: "",
        contact_designation: "",
        notes: `dice_${diceId}`,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        application_ids: [],
        file_id: null,
        user_id: "",
        profile_image_key: null,
        preferred_salary: null,
        line_address_1: null,
        line_address_2: null,
        zip_code: null,
        vendor_id: envConfig.vendorId,
        tenant_id: "",
        vendorID: envConfig.vendorId,
        designation: designation,
        employer: employer,
        experience: parseFloat(yearsExp) || 0,
        parsedResumeData: parsedResume,
        isFileBased: false,
        avatar: profileData.imageUrl || null,
      };
      return {
        candidate: candidate,
        files: [],
        profile_img_url: candidate.avatar,
      };
    } catch (error) {
      console.error("Error transforming Dice profile:", error);
      return null;
    }
  };

  const searchDiceCandidates = async (formValues, page = 1) => {
    try {
      if (page === 1) {
        setLoading(true);
        setHasSearched(true);
        setSearchResults(null);
        setSearchError(null);
        setAllDiceResults([]);
      } else {
        setIsLoadingMoreDice(true);
      }

      const paramsObj = {
        dice_access_token: diceAccessToken,
        page: page,
        pageSize: 10,
        q: formValues.searchKeywords || "",
        country: formValues.country || "",
        skills: (formValues.skillsAS || []).join(","),
        jobTitle: formValues.jobTitleAS || "",
        distance: formValues.distance || "",
        distanceUnit: formValues.distanceUnit || "",
        company: (formValues.company || []).join(","),
        workPermit: (formValues.visa || []).join(","),
        employmentType: (formValues.employmentType || []).join(","),
        lastActive: formValues.candidateLastActive || "",
        dateResumeLastUpdated: formValues.resumeLastUpdated || "",
        hasEmail: formValues.emailAS ? "true" : undefined,
        hasPhoneNumber: formValues.phoneNumberAS ? "true" : undefined,
        willingToRelocate: formValues.willingToRelocate ? "true" : undefined,
      };

      if (formValues.yearsofEXPFrom || formValues.yearsofEXPto) {
        const minExp = formValues.yearsofEXPFrom
          ? parseInt(formValues.yearsofEXPFrom)
          : "N/A";
        const maxExp = formValues.yearsofEXPto
          ? parseInt(formValues.yearsofEXPto)
          : "N/A";
        paramsObj.yearsExperience = `{"min":${minExp},"max":${maxExp}}`;
      }

      if (formValues.location) {
        const locationObj = { value: formValues.location };
        if (formValues.distance && formValues.distance.trim())
          locationObj.distance = formValues.distance;
        if (formValues.distanceUnit && formValues.distanceUnit.trim())
          locationObj.distanceUnit = formValues.distanceUnit;
        paramsObj.locations = JSON.stringify([locationObj]);
      }

      const params = new URLSearchParams();
      Object.entries(paramsObj).forEach(([key, value]) => {
        if (value !== "" && value !== undefined && value !== null) {
          params.append(key, value);
        }
      });

      const response = await get(
        DICE_SEARCH_BASE_URL,
        `${DICE_BASIC_SEARCH_URL}?${params.toString()}`,
      );

      const parsedData =
        typeof response === "string" ? JSON.parse(response) : response;

      if (parsedData.error === "No views remaining.") {
        setSearchResults(null);
        setSearchError("No views remaining.");
      } else {
        const newResults = parsedData.data || [];

        if (page === 1) {
          setAllDiceResults(newResults);
          setSearchResults({
            ...parsedData,
            data: newResults,
          });
        } else {
          setAllDiceResults((prev) => {
            const updated = [...prev, ...newResults];
            return updated;
          });
          setSearchResults((prev) => ({
            ...prev,
            data: [...prev.data, ...newResults],
          }));
        }

        const hasMore = newResults.length === 10;
        setHasMoreDiceResults(hasMore);
      }
    } catch (error) {
      if (error.response?.data?.error === "No views remaining.") {
        setSearchError("No views remaining.");
      } else {
        setSearchError("An unexpected error occurred.");
        setSearchResults(null);
      }
    } finally {
      if (page === 1) {
        setLoading(false);
      } else {
        setIsLoadingMoreDice(false);
      }
    }
  };

  const loadMoreDiceResults = () => {
    if (!hasMoreDiceResults || isLoadingMoreDice) {
      return;
    }
    const nextPage = diceCurrentPage + 1;
    setDiceCurrentPage(nextPage);
    searchDiceCandidates(formik.values, nextPage, true);
  };

  const toggleAdvancedSearch = () => {
    setShowAdvancedSearch(!showAdvancedSearch);
    formik.resetForm();
    setAdvancedSearchPayload(null);
    setShowPayloadPreview(false);
  };

  const getMapping = () => {
    try {
      const raw = localStorage.getItem("diceCandidateIdMap");
      return raw ? JSON.parse(raw) : {};
    } catch (_) {
      return {};
    }
  };

  const setMapping = (diceId, candidateId) => {
    const map = getMapping();
    map[diceId] = candidateId;
    localStorage.setItem("diceCandidateIdMap", JSON.stringify(map));
  };
  const decodeBase64ToFile = (base64, fileName, contentType) => {
    try {
      const byteChars = atob(base64);
      const byteNumbers = new Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++)
        byteNumbers[i] = byteChars.charCodeAt(i);
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], {
        type: contentType || "application/pdf",
      });
      const file = new File([blob], fileName || "resume.pdf", {
        type: contentType || "application/pdf",
      });
      file.localUrl = URL.createObjectURL(blob);
      return file;
    } catch (_) {
      return null;
    }
  };

  const handlePopupClose = () => {
    setHasCandidateId(false);
    setRealCandidateId();
    setIsImportFailed(false);
    if (abortController) {
      abortController.abort("Popup closed by user");
    }
    if (localCandidate?.files?.[0]?.localUrl) {
      URL.revokeObjectURL(localCandidate.files[0].localUrl);
    }
    setIsEditingCandidate(false);
    setOriginalCandidateData(null);
    setEditedCandidateData(null);
    setShowLocalCandidatePopup(false);
    setLocalCandidate(null);
    setFallbackDicePopupOpen(false);
    setSelectedCandidateId(null);
    setAbortController(null);
    setIsProcessingView(false);
  };

  const fetchDiceProfileWithAbort = async (id, signal) => {
    try {
      if (!diceAccessToken) return null;
      const res = await get(
        DICE_SEARCH_BASE_URL,
        `${DICE_CANDIDATE_PROFILE_VIEW}?dice_access_token=${diceAccessToken}&can_id=${id}`,
      );
      const data = typeof res === "string" ? JSON.parse(res) : res;
      if (data?.usage?.quota !== undefined) {
        setSearchResults((prevResults) => ({
          ...prevResults,
          usage: data.usage,
        }));
      }
      return data;
    } catch (e) {
      if (e.name === "AbortError") {
        console.log("Dice profile fetch aborted");
        return null;
      }
      return null;
    }
  };

  const handleViewCandidate = async (index) => {
    if (isProcessingView) {
      abortController?.abort();
    }
    const controller = new AbortController();
    setAbortController(controller);
    setIsProcessingView(true);
    setLocalCandidate(null);
    setShowLocalCandidatePopup(true);
    try {
      const candidate = searchResults?.data?.[index];
      if (!candidate) {
        console.error("Candidate not found at index:", index);
        return;
      }
      const diceCandidateId = candidate.candidateId;
      const appendToDiceCandidateId = "dice_" + String(diceCandidateId).trim();
      const token = localStorage.getItem("token");
      if (token) {
        try {
          const notesRes = await get(
            VYLABS_BASE_URL,
            `${GET_CANDIDATES_BY_NOTES}`,
            {
              params: { notes: appendToDiceCandidateId },
              headers: { Authorization: `Bearer ${token}` },
              signal: controller.signal,
            },
          );
          if(notesRes?.candidate?.id) {
            setHasCandidateId(true);
            setRealCandidateId(notesRes?.candidate?.id);
          }
          const data = notesRes;
          const mappedId =
            (data && data.id) ||
            (data && data.candidate && data.candidate.id) ||
            (Array.isArray(data) && data[0]?.id) ||
            null;
          if (mappedId && !controller.signal.aborted) {
            try {
              const full = await fetchCandidateWithVendor(mappedId);
              if (full) {
                setLocalCandidate(full);
                setIsProcessingView(false);
                return;
              }
            } catch (err) {
              console.log("Candidate fetch failed, fallback to Dice profile");
            }
          }
        } catch (err) {
          if (controller.signal.aborted) {
            console.log("Notes lookup aborted");
            return;
          }
        }
      }
      const profile = await fetchDiceProfileWithAbort(
        diceCandidateId,
        controller.signal,
      );
      setDiceProfileData(profile || null);

      const profileData =
        typeof profile === "string" ? JSON.parse(profile) : profile;
      const candidateRawName =
        candidate?.fullName ||
        profileData?.fullName ||
        `${profileData?.firstName || ""} ${profileData?.lastName || ""}`.trim() ||
        "";
      const htmlFileName = getCandidateFileName(candidateRawName, diceCandidateId, "html");

      const resume = profileData?.resume || profile?.resume;
      let tempResumeFile = null;
      if (resume?.resumeHtml) {
        tempResumeFile = htmlToFile(
          resume.resumeHtml,
          htmlFileName,
        );
      } else if (resume?.resumeData) {
        const fileName = resume?.filename || `dice_${diceCandidateId}.pdf`;
        const contentType = resume?.contentType || "application/pdf";
        tempResumeFile = decodeBase64ToFile(
          resume.resumeData,
          fileName,
          contentType,
        );
      }

      const transformedCandidate = transformDiceProfileToCandidate(
        profile,
        diceCandidateId,
      );
      if (transformedCandidate && !controller.signal.aborted) {
        const displayCandidate = {
          ...transformedCandidate.candidate,
          vendorID: envConfig.vendorId,
          vendorDetails: null,
          files: tempResumeFile ? [tempResumeFile] : transformedCandidate.files,
          presignedUrl: transformedCandidate.files[0]?.presigned_url || null,
          isFileBased: false,
          avatar: transformedCandidate.profile_img_url,
          parsedResumeData: transformedCandidate.candidate.parsedResumeData,
          isTransformed: true,
          source: "Dice",
          diceId: diceCandidateId,
          isLoadingResume: true,
          isTempCandidate: true,
          shouldSkipFetch: true,
        };
        setLocalCandidate(displayCandidate);
        setIsProcessingView(false);
        setFallbackDicePopupOpen(false);

        startBackgroundDiceResumeParsing(
          diceCandidateId,
          profile,
          controller.signal,
          displayCandidate,
          tempResumeFile,
        );
        return;
      }
      setIsProcessingView(false);
      setSelectedCandidateId(diceCandidateId);
      setFallbackDicePopupOpen(true);
    } catch (e) {
      if (controller.signal.aborted) {
        console.log("Operation aborted");
        return;
      }
      console.error("Error in handleViewCandidate:", e);
      const candidate = searchResults?.data?.[index];
      setSelectedCandidateId(candidate?.candidateId || null);
      setFallbackDicePopupOpen(true);
    } finally {
      setIsProcessingView(false);
      setAbortController(null);
    }
  };

  const startBackgroundDiceResumeParsing = async (
    diceId,
    profile,
    signal,
    displayCandidate,
    tempResumeFile,
  ) => {
    try {
      let resumeFile = tempResumeFile;
      if (!resumeFile) {
        const resume = profile?.resume;
        if (resume?.resumeHtml) {
          const profileData =
            typeof profile === "string" ? JSON.parse(profile) : profile;
          const candidateRawName =
            displayCandidate?.name ||
            profileData?.fullName ||
            `${profileData?.firstName || ""} ${profileData?.lastName || ""}`.trim() ||
            "";
          const fileName = getCandidateFileName(candidateRawName, diceId, "html");
          resumeFile = htmlToFile(resume.resumeHtml, fileName);
        } else if (resume?.resumeData) {
          const fileName = resume?.filename || `dice_${diceId}.pdf`;
          const contentType = resume?.contentType || "application/pdf";
          resumeFile = decodeBase64ToFile(
            resume.resumeData,
            fileName,
            contentType,
          );
        }
      }
      if (!resumeFile) {
        console.log(
          "[Background Parsing] No resume content to parse for Dice candidate. Creating candidate from profile data.",
        );
        try {
          if (
            !displayCandidate.name ||
            (!displayCandidate.email &&
            !displayCandidate.phone)
          ) {
            setLocalCandidate((prev) => ({ ...prev, isLoadingResume: false }));
            setIsImportFailed(true);
            return;
          }
          const payload = {
            name: displayCandidate.name,
            email: displayCandidate.email,
            phone: displayCandidate.phone,
            skills: displayCandidate.skills,
            yearsOfExperience: displayCandidate.experience,
            vendorID: envConfig.vendorId,
            status: "New Profile",
            notes: `dice_${diceId}`,
            source: "Dice",
          };
          const response = await post(VYLABS_BASE_URL, CCREATE_CAN, payload);
          if (response?.id) {
            setMapping(diceId, response.id);
            const full = await retryFetchCandidate(
              response.id,
              5,
              5000,
              signal,
            );
            if (full && !signal?.aborted) {
              setLocalCandidate((prev) => ({
                ...prev,
                ...full,
                isLoadingResume: false,
                isTempCandidate: false,
              }));
            } else if (!signal?.aborted) {
              setLocalCandidate((prev) => ({
                ...prev,
                isLoadingResume: false,
              }));
            }
            setHasCandidateId(true);
            setRealCandidateId(response.id);
          } else {
            setLocalCandidate((prev) => ({ ...prev, isLoadingResume: false }));
          }
        } catch (e) {
          console.error("Error creating candidate from profile data:", e);
          setLocalCandidate((prev) => ({ ...prev, isLoadingResume: false }));
        }
        return;
      }
      parseResumeHeadless(resumeFile, { signal })
        .then(async (parseResult) => {
          if (signal?.aborted) return;
          if (parseResult?.candidateId) {
            console.log("Completed file detected");
            try {
              const payload = {
                id: parseResult.candidateId,
                vendorID: envConfig.vendorId,
                notes: `dice_${diceId}`,
                name: displayCandidate.name,
                email: displayCandidate.email,
                phone: displayCandidate.phone,
                skills: displayCandidate.skills,
                city: displayCandidate.city,
                state: displayCandidate.state,
                country: displayCandidate.country,
                designation: displayCandidate.designation,
                yearsOfExperience: displayCandidate.experience,
                source: "Dice",
              };
              Object.keys(payload).forEach((key) => {
                if (payload[key] === "" || payload[key] == null) {
                  delete payload[key];
                }
              });
              await post(VYLABS_BASE_URL, UPDATE_CANDIDATE_DETAILS, payload, {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem("token")}`,
                },
              });
            } catch (e) {
              console.error("Failed to update candidate with dice notes", e);
            }
            const full = await retryFetchCandidate(
              parseResult.candidateId,
              5,
              5000,
              signal,
            );
            if (!full || signal?.aborted) {
              if (!signal?.aborted)
                setLocalCandidate((prev) => ({
                  ...prev,
                  isLoadingResume: false,
                }));
              return;
            }
            setLocalCandidate((prev) => ({
              ...prev,
              ...full,
              isLoadingResume: false,
              isTempCandidate: false,
            }));
            setHasCandidateId(true);
            setRealCandidateId(parseResult.candidateId);
            return;
          }
          if (parseResult?.status === "INCOMPLETE") {
            console.log("Incomplete file detected");
            if (
              !displayCandidate.name || (
              !displayCandidate.email &&
              !displayCandidate.phone)
            ) {
              setLocalCandidate((prev) => ({
                ...prev,
                isLoadingResume: false,
              }));
              setIsImportFailed(true);
              return;
            }
            const payload = {
              name: displayCandidate.name,
              email: displayCandidate.email,
              phone: displayCandidate.phone,
              skills: displayCandidate.skills,
              city: displayCandidate.city,
              state: displayCandidate.state,
              country: displayCandidate.country,
              designation: displayCandidate.designation,
              yearsOfExperience: displayCandidate.experience,
              vendorID: envConfig.vendorId,
              status: "New Profile",
              notes: `dice_${diceId}`,
              fileID: parseResult.fileID,
              source: "Dice",
            };
            const response = await post(VYLABS_BASE_URL, CCREATE_CAN, payload);
            if (response?.id) {
              setMapping(diceId, response.id);
              const full = await retryFetchCandidate(
                response.id,
                5,
                5000,
                signal,
              );
              if (full && !signal?.aborted) {
                setLocalCandidate((prev) => ({
                  ...prev,
                  ...full,
                  isLoadingResume: false,
                  isTempCandidate: false,
                }));
              } else if (!signal?.aborted) {
                setLocalCandidate((prev) => ({
                  ...prev,
                  isLoadingResume: false,
                }));
              }
              setHasCandidateId(true);
              setRealCandidateId(response.id);
            } else {
              setLocalCandidate((prev) => ({
                ...prev,
                isLoadingResume: false,
              }));
            }
            return;
          }
          if (parseResult?.status === "FAILED") {
            console.log("Failed file detected");
            if (
              !displayCandidate.name || (
              !displayCandidate.email &&
              !displayCandidate.phone)
            ) {
              setLocalCandidate((prev) => ({
                ...prev,
                isLoadingResume: false,
              }));
              setIsImportFailed(true);
              return;
            }
            const payload = {
              name: displayCandidate.name,
              email: displayCandidate.email,
              phone: displayCandidate.phone,
              skills: displayCandidate.skills,
              city: displayCandidate.city,
              state: displayCandidate.state,
              country: displayCandidate.country,
              designation: displayCandidate.designation,
              yearsOfExperience: displayCandidate.experience,
              status: "New Profile",
              vendorID: envConfig.vendorId,
              notes: `dice_${diceId}`,
              source: "Dice",
            };
            const response = await post(VYLABS_BASE_URL, CCREATE_CAN, payload);
            if (response?.id) {
              setMapping(diceId, response.id);
              try {
                await post(VYLABS_BASE_URL, LINK_CANDIDATE_RESUME, {
                  candidate_id: response.id,
                  file_id: parseResult.fileID,
                });
              } catch (e) {
                console.error("Failed to link resume for failed parse", e);
              }
              const full = await retryFetchCandidate(
                response.id,
                5,
                5000,
                signal,
              );
              if (full && !signal?.aborted) {
                setLocalCandidate((prev) => ({
                  ...prev,
                  ...full,
                  isLoadingResume: false,
                  isTempCandidate: false,
                }));
              } else if (!signal?.aborted) {
                setLocalCandidate((prev) => ({
                  ...prev,
                  isLoadingResume: false,
                }));
              }
              setHasCandidateId(true);
              setRealCandidateId(response.id);
            } else {
              setLocalCandidate((prev) => ({
                ...prev,
                isLoadingResume: false,
              }));
            }
            return;
          }
          console.warn(
            "[Background Parsing] Unhandled parse result:",
            parseResult,
          );
          setLocalCandidate((prev) => ({ ...prev, isLoadingResume: false }));
        })
        .catch((err) => {
          if (signal?.aborted) return;
          console.error("Background parsing error:", err);
          setLocalCandidate((prev) => ({ ...prev, isLoadingResume: false }));
        });
    } catch (error) {
      if (signal?.aborted) return;
      console.error("[Background Parsing] Setup error:", error);
      setLocalCandidate((prev) => ({ ...prev, isLoadingResume: false }));
    }
  };

  const fetchCandidateWithVendor = async (id) => {
    try {
      const res = await get(VYLABS_BASE_URL, `${GET_CANDIDATE_BY_ID}?id=${id}`);
      const candidate = res?.candidate;
      const candidatefiles = res?.files;
      const parsedResumeData = candidate?.parsed_resume
        ? JSON.parse(candidate.parsed_resume)
        : null;
      const presignedUrl =
        candidatefiles && candidatefiles.length > 0
          ? candidatefiles[0].presigned_url
          : null;

      const updatedCandidate = {
        ...candidate,
        vendorID: candidate.vendor_id,
        parsedResumeData,
        files: candidatefiles || null,
        presignedUrl,
        isFileBased: false,
        avatar: res?.profile_img_url,
      };

      if (updatedCandidate.vendorID) {
        try {
          const vendorResponse = await get(
            VYLABS_BASE_URL,
            `${GET_VENDOR_DETAILS_BY_ID}?id=${updatedCandidate.vendorID}`,
          );
          setAssignedVendor(vendorResponse);
          updatedCandidate.vendorDetails = vendorResponse?.vendor || null;
        } catch (err) {
          console.error("Error fetching vendor details:", err);
          updatedCandidate.vendorDetails = null;
          setAssignedVendor(null);
        }
      } else {
        updatedCandidate.vendorDetails = null;
        setAssignedVendor(null);
      }
      return updatedCandidate;
    } catch (error) {
      console.error("Error fetching candidate:", error);
      return null;
    }
  };
  const retryFetchCandidate = async (
    candidateId,
    attempts = 5,
    delayMs = 5000,
    signal = null,
  ) => {
    for (let i = 0; i < attempts; i++) {
      if (signal?.aborted) {
        console.log("Retry fetch candidate aborted");
        return null;
      }

      const full = await fetchCandidateWithVendor(candidateId);
      if (full && full.id) return full;

      if (signal?.aborted) return null;
      await new Promise((r) => setTimeout(r, delayMs));
    }
    return null;
  };
  return (
    <div className="dice-search-page-wrapper">
      <div className="header-main-container">
        <div className="header-icon-title-container">
          <div className="dice-header-container">
            <div className="dice-header-and-icon">
              <img
                alt=""
                src={isDarkMode ? diceDarkIcon : diceIcon}
                className="header-title-icon"
              ></img>
              <h2 className="header-container">Dice Search</h2>
            </div>
          </div>
        </div>
        <div className="dice-account-bar">
          {/* <SelectInput
            options={accountOptions}
            value={selectedAccountEmail}
            onChange={(e) => setSelectedAccountEmail(e.target.value)}
            required={true}
            wrapperStyle={{ width: "300px" }}
          /> */}
          {/* <Button
            label={isSwitchingAccount ? "Switching..." : "Switch Account"}
            event={handleSwitchAccount}
            disabled={isSwitchingAccount}
          /> */}
          <Button
            label="Back to Vendor"
            event={() => navigate("/vendors")}
            iconSrc={backArrowIcon}
          />
        </div>
      </div>

      <div className="dice-outer-main-container">
        <div className="advanced-search-toggle">
          <h4 onClick={toggleAdvancedSearch} className="advance-search-text">
            {showAdvancedSearch ? "Hide Advanced Search" : "Advanced Search"}
          </h4>
          <CustomTooltip
            title={
              showAdvancedSearch
                ? "It Can Hide Advance Search Fields"
                : "It Can Show More Fields For Advance Search"
            }
            arrow
          >
            <img alt="infoIcon" src={infoIcon} />
          </CustomTooltip>
        </div>
        <div className="dice-main-search-container">
          <TextInput
            label="Keyword"
            required={true}
            placeholder="Enter your search keywords"
            name="searchKeywords"
            value={formik.values.searchKeywords}
            onChange={formik.handleChange}
          />
          <TextInput
            label="Location"
            placeholder="Enter your search location"
            name="location"
            value={formik.values.location}
            onChange={formik.handleChange}
          />
        </div>
        {showAdvancedSearch && (
          <>
            <div className="skills-autocomplete" style={{ marginTop: "10px" }}>
              <label htmlFor="title" className="skills-label">
                Skills
              </label>
              <AutoComplete
                key={`auto-complete-${autoCompleteKey}`}
                multiple
                placeholder="e.g. JavaScript, React, Node.js"
                options={skills.filter((skill) => {
                  const selectedSkills = Array.isArray(formik.values.skillsAS)
                    ? formik.values.skillsAS
                    : [];
                  return !selectedSkills.some(
                    (selectedSkill) =>
                      selectedSkill.toLowerCase() === skill.title.toLowerCase(),
                  );
                })}
                tags={true}
                name="skillsAS"
                style={{ marginTop: "15px !important" }}
                value={formik.values.skillsAS || []}
                onChange={(selectedOptions) => {
                  const validSkills = selectedOptions
                    .map((option) =>
                      typeof option === "string" ? option : option.title,
                    )
                    .filter((option) => option.trim() !== "");

                  const uniqueSkills = validSkills.filter(
                    (skill, index, self) =>
                      index ===
                      self.findIndex(
                        (s) => s.toLowerCase() === skill.toLowerCase(),
                      ),
                  );

                  formik.setFieldValue("skillsAS", uniqueSkills);
                }}
                onBlur={() => formik.setFieldTouched("skillsAS", true)}
                InputWrapperStyle={{
                  height: "100px",
                  overflowY: "auto",
                  padding: "10px",
                  marginBottom: "30px",
                  marginTop: "15px",
                }}
                showClearAll={true}
                error={formik.touched.skillsAS && formik.errors.skillsAS}
                errorMessage={formik.errors.skillsAS}
              />
            </div>
            <div className="dice-advance-search-container">
              <TextInput
                label="Job Title"
                placeholder="Enter your search job title"
                name="jobTitleAS"
                value={formik.values.jobTitleAS}
                onChange={formik.handleChange}
              />
              <div className="exp-from-to">
                <TextInput
                  label="From Experience"
                  placeholder={`Enter your search "FROM" experience`}
                  name="yearsofEXPFrom"
                  value={formik.values.yearsofEXPFrom}
                  onChange={formik.handleChange}
                  containerStyle={{ width: "100%" }}
                />
                <TextInput
                  label="To Experience"
                  placeholder={`Enter your search "TO" experience`}
                  name="yearsofEXPto"
                  value={formik.values.yearsofEXPto}
                  onChange={formik.handleChange}
                  containerStyle={{ width: "100%" }}
                />
              </div>
              <TextInput
                label="Distance"
                placeholder="Enter your search distance"
                name="distance"
                value={formik.values.distance}
                onChange={formik.handleChange}
              />
              <SelectInput
                label="Distance Unit"
                options={DistanceUnit}
                name="distanceUnit"
                value={formik.values.distanceUnit}
                onChange={formik.handleChange}
                wrapperStyle={{
                  display: "flex",
                  width: "100%",
                  marginBottom: "15px",
                }}
                labelStyle={{
                  fontSize: "16px",
                  fontWeight: 600,
                  width: "fit-content",
                  marginBottom: "12px",
                }}
              />
              <TextInput
                label="Social Profiles"
                placeholder="Enter your search social profiles"
                name="socialProfiles"
                value={formik.values.socialProfiles}
                onChange={formik.handleChange}
              />
              <DynamicMultipleSelectCheckmarks
                options={DiceCompany}
                label="Company"
                placeholder="Select company(s)"
                value={formik.values.company}
                onChange={(values) => formik.setFieldValue("company", values)}
              />
              <DynamicMultipleSelectCheckmarks
                options={DiceWorkPermitVisa}
                label="Work Permit / Visa"
                placeholder="Select Work Permit / Visa"
                value={formik.values.visa}
                onChange={(values) => formik.setFieldValue("visa", values)}
              />
              <DynamicMultipleSelectCheckmarks
                options={DiceEmploymentType}
                label="Employment Type"
                placeholder="Select Employment Type"
                value={formik.values.employmentType}
                onChange={(values) =>
                  formik.setFieldValue("employmentType", values)
                }
              />
              <TextInput
                label="Last Active Day(s)"
                placeholder="Enter your last active in day(s)"
                name="candidateLastActive"
                value={formik.values.candidateLastActive}
                onChange={formik.handleChange}
              />
              <TextInput
                label="Resume Last Updated"
                placeholder="Enter your Resume Last Updated in day(s)"
                name="resumeLastUpdated"
                value={formik.values.resumeLastUpdated}
                onChange={formik.handleChange}
              />
            </div>
            <div className="check-box-container">
              <DynamicCheckbox
                label={"Email"}
                name="emailAS"
                checked={formik.values.emailAS}
                onChange={formik.handleChange}
                sx={{ padding: "0 !important", marginRight: "5px" }}
              />
              <DynamicCheckbox
                label={"Phone Number"}
                name="phoneNumberAS"
                checked={formik.values.phoneNumberAS}
                onChange={formik.handleChange}
                sx={{ padding: "0 !important", marginRight: "5px" }}
              />
              <DynamicCheckbox
                label={"Willing to Relocate"}
                name="willingToRelocate"
                checked={formik.values.willingToRelocate}
                onChange={formik.handleChange}
                sx={{ padding: "0 !important", marginRight: "5px" }}
              />
            </div>
          </>
        )}
        <div className="dice-search-button-container">
          <Button
            label={
              loading
                ? showAdvancedSearch
                  ? "Preparing..."
                  : "Searching..."
                : showAdvancedSearch
                  ? "Search Candidates"
                  : "Search Candidates"
            }
            event={formik.handleSubmit}
          />
          <div className="candidate-results-container">
            {!diceAccessToken ? (
              <div
                className="dice-no-search-result"
                style={{ textAlign: "center", marginTop: 20 }}
              >
                <p
                  style={{
                    fontWeight: "bold",
                    color: "var(--header__color)",
                    fontSize: "1.2rem",
                  }}
                >
                  Please select an account to search
                </p>
                <AnimationPlayer
                  animationSrc={NoDataFound}
                  playerHeight="250px"
                  playerWidth="250px"
                />
              </div>
            ) : (
              <>
                {searchResults?.usage && (
                  <div className="dice-search-stats-and-toggle">
                    <div className="dice-search-usage-stats">
                    <span>
                      <strong>Total views:</strong> {searchResults.usage.quota}
                    </span>
                    {" , "}
                    <span>
                      <strong>Views Consumed:</strong>
                      {searchResults.usage.viewsConsumed}
                    </span>
                    {" , "}
                    <span>
                      <strong>Views Remaining:</strong>
                      {searchResults.usage.viewsRemaining}
                    </span>
                    </div>
                    <div className="dice-card-toggle-group">
                      <Button
                        label="Compact Card"
                        iconSrc={compactIcon}
                        iconPosition="left"
                        iconWidth="14px"
                        hoverd={false}
                        className={`dice-toggle-btn ${cardView === "compact" ? "active" : ""}`}
                        event={() => setCardView("compact")}
                        backgroundColor={cardView === "compact" ? "#6860ee" : "transparent"}
                        color={cardView === "compact" ? "#ffffff" : "var(--grid-na-text, #70707D)"}
                      />
                      <Button
                        label="Detailed Card"
                        iconSrc={detailedIcon}
                        iconPosition="left"
                        iconWidth="14px"
                        hoverd={false}
                        className={`dice-toggle-btn ${cardView === "detailed" ? "active" : ""}`}
                        event={() => setCardView("detailed")}
                        backgroundColor={cardView === "detailed" ? "#6860ee" : "transparent"}
                        color={cardView === "detailed" ? "#ffffff" : "var(--grid-na-text, #70707D)"}
                      />
                    </div>
                  </div>
                )}
                {loading && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      minHeight: "40vh",
                    }}
                  >
                    <VybogTALLoaderAnimation />
                  </div>
                )}
                {!loading && searchError === "No views remaining." ? (
                  <div
                    className="dice-no-search-result"
                    style={{ textAlign: "center", marginTop: 20 }}
                  >
                    <p
                      style={{
                        fontWeight: "bold",
                        color: "red",
                        fontSize: "1.2rem",
                      }}
                    >
                      Your monthly resume view count has been reached. Please
                      try switching to a different account.
                    </p>
                    <AnimationPlayer
                      animationSrc={NoDataFound}
                      playerHeight="250px"
                      playerWidth="250px"
                    />
                  </div>
                ) : !loading && searchResults?.data?.length > 0 ? (
                  <div
                    className={`overall-card-container ${cardView === "detailed" ? "detailed-view" : ""}`}
                  >
                    {searchResults.data.map((candidate, index) => {
                      const isLast = index === searchResults.data.length - 1;
                      const isDetailed = cardView === "detailed";

                      const getPreferenceValue = (preferences, field) => {
                        if (!Array.isArray(preferences)) return undefined;
                        for (const pref of preferences) {
                          if (!pref) continue;
                          const val = pref[field];
                          if (val !== undefined && val !== null) {
                            if (typeof val === "object") {
                              if (
                                (val.min && Number(val.min) > 0) ||
                                (val.max && Number(val.max) > 0) ||
                                val.value
                              ) {
                                return val;
                              }
                            } else if (val !== "") {
                              return val;
                            }
                          }
                        }
                        return undefined;
                      };

                      const candidateAnnualSalary = isDetailed
                        ? getPreferenceValue(candidate.workPreferences, "annualSalary") || candidate.annualSalary
                        : null;

                      const candidateHourlyRate = isDetailed
                        ? getPreferenceValue(candidate.workPreferences, "hourlyRate") || candidate.hourlyRate
                        : null;

                      const candidateWillingToRelocate = isDetailed
                        ? (() => {
                            const prefRelocate = candidate.workPreferences?.find(
                              (wp) => wp && wp.willingToRelocate !== undefined && wp.willingToRelocate !== null
                            )?.willingToRelocate;
                            return prefRelocate !== undefined ? prefRelocate : candidate.willingToRelocate;
                          })()
                        : undefined;
                      const candidateEmploymentTypes = isDetailed
                        ? (() => {
                            if (Array.isArray(candidate.workPreferences)) {
                              const types = candidate.workPreferences.flatMap((wp) => {
                                if (Array.isArray(wp?.employmentType)) return wp.employmentType;
                                if (typeof wp?.employmentType === "string") return [wp.employmentType];
                                return [];
                              });
                              if (types.length > 0) {
                                return [...new Set(types.map((t) => String(t).trim()).filter(Boolean))];
                              }
                            }
                            if (Array.isArray(candidate.employmentType)) {
                              return [
                                ...new Set(
                                  candidate.employmentType
                                    .map((t) => String(t).trim())
                                    .filter(Boolean)
                                ),
                              ];
                            }
                            if (
                              typeof candidate.employmentType === "string" &&
                              candidate.employmentType.trim()
                            ) {
                              return [candidate.employmentType.trim()];
                            }
                            return [];
                          })()
                        : [];
                      return (
                        <div
                          key={candidate.id || candidate.candidateId || index}
                          ref={isLast ? lastCardRef : null}
                        >
                          <DiceCard
                            cardView={cardView}
                            name={candidate.fullName}
                            role={candidate.currentJobTitle || "Not specified"}
                            experience={candidate.yearsOfExperience || 0}
                            location={
                              candidate.location?.name ||
                              [candidate.city, candidate.region, candidate.country]
                                .filter(Boolean)
                                .join(", ") ||
                              "N/A"
                            }
                            workPermitDocuments={
                              isDetailed
                                ? (candidate.workPermitDocuments?.length
                                    ? candidate.workPermitDocuments
                                    : (candidate.usCitizen ? ["US Citizen"] : []))
                                : []
                            }
                            showVisaStatus={isDetailed}
                            skills={
                              candidate.skills
                                ?.map((s) => s.skill)
                                .filter(Boolean) || []
                            }
                            viewEvent={() => handleViewCandidate(index)}
                            showView={true}
                            showAssignJob={false}
                            cardbackgroundColor="var(--card__background)"
                            cardColor="var(--header__color)"
                            skillbackgroundColor="var(--skill-badge-bg)"
                            skillColor="var(--skill-badge-color)"
                            dateLastActive={isDetailed ? candidate.dateLastActive : null}
                            dateResumeLastUpdated={
                              isDetailed
                                ? candidate.dateResumeLastUpdated || candidate.dateLastUpdated
                                : null
                            }
                            employmentType={candidateEmploymentTypes}
                            annualSalary={candidateAnnualSalary}
                            hourlyRate={candidateHourlyRate}
                            willingToRelocate={candidateWillingToRelocate}
                            isDetailed={isDetailed}
                          />
                        </div>
                      );
                    })}
                    {isLoadingMoreDice && (
                      <>
                        {Array.from({ length: 10 }).map((_, index) => (
                          <DynamicSkeleton
                            key={index}
                            variant="rectangular"
                            dimensions={{
                              width: "100%",
                              height: "170px",
                              sx: { borderRadius: "16px" },
                            }}
                          />
                        ))}
                      </>
                    )}
                    {!hasMoreDiceResults && searchResults.data.length > 10 && (
                      <div
                        style={{
                          textAlign: "center",
                          margin: "20px 0",
                          color: "#666",
                        }}
                      >
                        <p>No more candidates to load</p>
                      </div>
                    )}
                    <SidePopup
                      isOpen={showLocalCandidatePopup}
                      onClose={handlePopupClose}
                      sidePopupWidth="82%"
                      showHeader={true}
                      title="Candidate Details"
                    >
                      {isProcessingView && (
                        <div
                          className="dice-view-load"
                          style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            height: "80vh",
                            width: "100%",
                          }}
                        >
                          <VybogTALLoaderAnimation />
                        </div>
                      )}
                      {localCandidate?.id && (
                        <CandidateDetailsContainer
                          key={localCandidate?.id}
                          candidate={localCandidate}
                          activeTab={0}
                          handleTabChange={() => {}}
                          hasSearched={false}
                          isEditingCandidate={isEditingCandidate}
                          handleEditCandidate={handleEditCandidate}
                          saveCandidateChanges={saveCandidateChanges}
                          resetCandidateEdit={resetCandidateEdit}
                          handleCandidateChange={handleCandidateChange}
                          editedCandidateData={editedCandidateData}
                          vendorList={vendorList}
                          vendorOptionLoading={vendorOptionLoading}
                          handleVendorInputChange={handleVendorInputChange}
                          assignedVendor={assignedVendor}
                          hasCandidateId={hasCandidateId}
                          candidateId={realCandidateId}
                          failedDueToMissingData={isImportFailed}
                        />
                      )}
                    </SidePopup>
                  </div>
                ) : (
                  !loading &&
                  hasSearched &&
                  ((searchError && searchError !== "No views remaining.") ||
                    searchResults?.data?.length === 0 ||
                    searchResults?.meta?.totalCount === 0) && (
                    <div className="dice-no-search-result">
                      <h3>
                        No candidates were found in the results of your search.
                      </h3>
                      <AnimationPlayer
                        animationSrc={NoDataFound}
                        playerHeight="300px"
                        playerWidth="300px"
                      />
                    </div>
                  )
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default DiceSearch;